
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ConnectionProcessingState, PROCESSING_STATE } from '../../constants';
import { logger } from '@/services/LoggingService';

interface ErrorDetails {
  message: string;
  code?: string;
  details?: any;
}

export interface ProcessingContextState {
  state: ConnectionProcessingState;
  error: ErrorDetails | null;
  isProcessing: boolean;
  provider?: string;
  connectionId?: string;
  updateState: (newState: ConnectionProcessingState, options?: { error?: ErrorDetails, provider?: string, connectionId?: string }) => void;
  resetState: () => void;
}

const ProcessingContext = createContext<ProcessingContextState | undefined>(undefined);

export const ProcessingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConnectionProcessingState>(PROCESSING_STATE.IDLE);
  const [error, setError] = useState<ErrorDetails | null>(null);
  const [provider, setProvider] = useState<string | undefined>(undefined);
  const [connectionId, setConnectionId] = useState<string | undefined>(undefined);
  
  // Determine if in a processing state
  const isProcessing = state !== PROCESSING_STATE.IDLE && 
                       state !== PROCESSING_STATE.COMPLETE && 
                       state !== PROCESSING_STATE.FAILED &&
                       state !== PROCESSING_STATE.ERROR;

  // Update the state with optional error and metadata
  const updateState = (
    newState: ConnectionProcessingState, 
    options?: { 
      error?: ErrorDetails, 
      provider?: string, 
      connectionId?: string 
    }
  ) => {
    logger.debug('ProcessingContext', 'State transition', {
      from: state,
      to: newState,
      provider: options?.provider || provider,
      connectionId: options?.connectionId || connectionId,
      hasError: !!options?.error
    });
    
    setState(newState);
    
    if (options?.error) {
      setError(options.error);
    } else if (newState !== PROCESSING_STATE.ERROR && newState !== PROCESSING_STATE.FAILED) {
      setError(null);
    }
    
    if (options?.provider) {
      setProvider(options.provider);
    }
    
    if (options?.connectionId) {
      setConnectionId(options.connectionId);
    }
  };
  
  // Reset state to idle
  const resetState = () => {
    logger.debug('ProcessingContext', 'Resetting state');
    setState(PROCESSING_STATE.IDLE);
    setError(null);
    setProvider(undefined);
    setConnectionId(undefined);
  };
  
  return (
    <ProcessingContext.Provider 
      value={{ 
        state, 
        error, 
        isProcessing, 
        provider, 
        connectionId,
        updateState, 
        resetState 
      }}
    >
      {children}
    </ProcessingContext.Provider>
  );
};

/**
 * Hook for accessing the Vezgo processing context
 * Export with consistent naming pattern
 */
export const useProcessingContext = (): ProcessingContextState => {
  const context = useContext(ProcessingContext);
  
  if (context === undefined) {
    throw new Error('useProcessingContext must be used within a ProcessingProvider');
  }
  
  return context;
};
